const API_URL = process.env.REACT_APP_BACKEND_URL;

class ApiClient {
  constructor() {
    this.token = localStorage.getItem('token');
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  }

  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }
    return headers;
  }

  async request(endpoint, options = {}) {
    const url = `${API_URL}${endpoint}`;
    const config = {
      ...options,
      headers: {
        ...this.getHeaders(),
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.detail || `Error: ${response.status}`);
      }

      return data;
    } catch (error) {
      throw error;
    }
  }

  // Auth
  async register(email, password) {
    const data = await this.request('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    this.setToken(data.token);
    return data;
  }

  async registerWithName(name, email, password, referralCode, couponCode) {
    const body = { name, email, password };
    if (referralCode) body.referral_code = referralCode;
    if (couponCode) body.coupon_code = couponCode;
    
    const data = await this.request('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    this.setToken(data.token);
    return data;
  }

  async registerWithInvite(name, email, password, inviteCode, referralCode, couponCode) {
    const body = { name, email, password, invite_code: inviteCode };
    if (referralCode) body.referral_code = referralCode;
    if (couponCode) body.coupon_code = couponCode;
    
    const data = await this.request('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    this.setToken(data.token);
    return data;
  }

  async login(email, password) {
    const data = await this.request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    this.setToken(data.token);
    return data;
  }

  async getMe() {
    return this.request('/api/auth/me');
  }

  async updateProfile(data) {
    return this.request('/api/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async changePassword(data) {
    return this.request('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  logout() {
    this.setToken(null);
  }

  // Frequencies
  async getFrequencies() {
    return this.request('/api/frequencies');
  }

  async createFrequency(data) {
    return this.request('/api/frequencies', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateFrequency(id, data) {
    return this.request(`/api/frequencies/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteFrequency(id) {
    return this.request(`/api/frequencies/${id}`, {
      method: 'DELETE',
    });
  }

  async uploadAudio(frequencyId, audioFile, isPremium = false) {
    const formData = new FormData();
    formData.append('audio', audioFile);
    formData.append('is_premium', isPremium);

    const url = `${process.env.REACT_APP_BACKEND_URL}/api/frequencies/${frequencyId}/upload-audio`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`,
      },
      body: formData,
    });

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(data?.detail || `Error: ${response.status}`);
    }

    return data;
  }

  async deleteAudio(frequencyId, filename) {
    return this.request(`/api/frequencies/${frequencyId}/audio/${filename}`, {
      method: 'DELETE',
    });
  }

  getAudioUrl(filename) {
    if (!filename) return null;
    return `${process.env.REACT_APP_BACKEND_URL}/api/audio/${filename}`;
  }

  // General Audio
  async getGeneralAudio() {
    return this.request('/api/admin/general-audio');
  }

  async uploadGeneralAudio(audioFile, isPremium = false) {
    const formData = new FormData();
    formData.append('audio', audioFile);
    formData.append('is_premium', isPremium);

    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/general-audio/upload`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`,
      },
      body: formData,
    });

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(data?.detail || `Error: ${response.status}`);
    }

    return data;
  }

  async deleteGeneralAudio(filename) {
    return this.request(`/api/admin/general-audio/${filename}`, {
      method: 'DELETE',
    });
  }

  // User Preferences
  async getPreferences() {
    return this.request('/api/user/preferences');
  }

  async updatePreferences(data) {
    return this.request('/api/user/preferences', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // User Active Frequency
  async getActiveFrequency() {
    return this.request('/api/user/active-frequency');
  }

  async setActiveFrequency(frequencyId) {
    return this.request('/api/user/active-frequency', {
      method: 'PUT',
      body: JSON.stringify({ frequency_id: frequencyId }),
    });
  }

  // User Favorites
  async getFavorites() {
    return this.request('/api/user/favorites');
  }

  async addToFavorites(frequencyId) {
    return this.request(`/api/user/favorites/${frequencyId}`, {
      method: 'POST',
    });
  }

  async removeFromFavorites(frequencyId) {
    return this.request(`/api/user/favorites/${frequencyId}`, {
      method: 'DELETE',
    });
  }

  // User History & Stats
  async getHistory() {
    return this.request('/api/user/history');
  }

  async getStats() {
    return this.request('/api/user/stats');
  }

  // User Notes
  async getNotes(frequencyId = null) {
    const url = frequencyId 
      ? `/api/user/notes?frequency_id=${frequencyId}` 
      : '/api/user/notes';
    return this.request(url);
  }

  async createNote(data) {
    return this.request('/api/user/notes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateNote(noteId, data) {
    return this.request(`/api/user/notes/${noteId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteNote(noteId) {
    return this.request(`/api/user/notes/${noteId}`, {
      method: 'DELETE',
    });
  }

  // Frequency Programs
  async getPrograms() {
    return this.request('/api/programs');
  }

  async createProgram(data) {
    return this.request('/api/programs', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateProgram(id, data) {
    return this.request(`/api/programs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteProgram(id) {
    return this.request(`/api/programs/${id}`, {
      method: 'DELETE',
    });
  }

  // Onboarding
  async completeOnboarding(data) {
    return this.request('/api/user/onboarding', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Admin - User Management
  async getAllUsers() {
    return this.request('/api/admin/users');
  }

  async createUserByAdmin(data) {
    return this.request('/api/admin/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUserByAdmin(userId, data) {
    return this.request(`/api/admin/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteUserByAdmin(userId) {
    return this.request(`/api/admin/users/${userId}`, {
      method: 'DELETE',
    });
  }

  async loginAsUser(userId) {
    return this.request(`/api/admin/login-as/${userId}`, {
      method: 'POST',
    });
  }

  async getAdminStats() {
    return this.request('/api/admin/stats');
  }

  // Order & Payment System
  async createOrder(orderData) {
    return this.request('/api/orders/create', {
      method: 'POST',
      body: JSON.stringify(orderData),
    });
  }

  async getOrders(limit = 50) {
    return this.request(`/api/orders?limit=${limit}`);
  }

  async getOrder(orderId) {
    return this.request(`/api/orders/${orderId}`);
  }

  async capturePayPalPayment(paymentId, payerId, orderId) {
    return this.request('/api/paypal/capture-payment', {
      method: 'POST',
      body: JSON.stringify({ payment_id: paymentId, payer_id: payerId, order_id: orderId }),
    });
  }

  async upgradeSubscription(newTier, billingInterval) {
    return this.request('/api/subscriptions/upgrade', {
      method: 'POST',
      body: JSON.stringify({ new_tier: newTier, billing_interval: billingInterval }),
    });
  }

  async pauseSubscription() {
    return this.request('/api/subscriptions/pause', { method: 'POST' });
  }

  async resumeSubscription() {
    return this.request('/api/subscriptions/resume', { method: 'POST' });
  }

  async cancelSubscription(reason = null) {
    return this.request('/api/subscriptions/cancel', {
      method: 'POST',
      body: reason ? JSON.stringify({ reason }) : null,
    });
  }

  async getMySubscription() {
    return this.request('/api/subscriptions/me');
  }

  // Admin: Payment Settings
  async getPaymentSettings() {
    return this.request('/api/admin/payment-settings');
  }

  async getPublicPaymentSettings() {
    // Public endpoint - no auth required
    return this.request('/api/payment-settings/public');
  }

  async updatePaymentSettings(settings) {
    return this.request('/api/admin/payment-settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  async testPayPalConnection() {
    return this.request('/api/admin/payment-settings/test-paypal', { method: 'POST' });
  }

  async confirmBankTransfer(orderId, adminNotes = null) {
    return this.request('/api/admin/bank-transfer/confirm', {
      method: 'POST',
      body: JSON.stringify({ order_id: orderId, admin_notes: adminNotes }),
    });
  }

  async manuallyCreditCoins(orderId) {
    return this.request(`/api/admin/orders/${orderId}/credit-coins`, {
      method: 'POST',
    });
  }

  async updateOrderStatus(orderId, status, adminNotes = null) {
    return this.request(`/api/admin/orders/${orderId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, admin_notes: adminNotes }),
    });
  }

  // Admin: Analytics
  async getDashboardAnalytics() {
    return this.request('/api/admin/analytics/dashboard');
  }

  async getRevenueAnalytics(startDate = null, endDate = null) {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    return this.request(`/api/admin/analytics/revenue?${params}`);
  }

  async getSubscriptionAnalytics() {
    return this.request('/api/admin/analytics/subscriptions');
  }

  async getConversionAnalytics() {
    return this.request('/api/admin/analytics/conversions');
  }

  // Admin: Subscription Management
  async getAllSubscriptions() {
    return this.request('/api/admin/subscriptions');
  }

  async setUserToTrial(userId) {
    return this.request('/api/admin/subscriptions/set-trial', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId })
    });
  }

  async cancelUserSubscription(userId, reason = 'Cancelled by admin') {
    return this.request(`/api/admin/subscriptions/${userId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason })
    });
  }

  async reactivateUserSubscription(userId) {
    return this.request(`/api/admin/subscriptions/${userId}/reactivate`, {
      method: 'POST'
    });
  }

  // Onetime Products
  async getOnetimeProducts() {
    return this.request('/api/onetime-products');
  }

  async getOnetimeProduct(productId) {
    return this.request(`/api/onetime-products/${productId}`);
  }

  async purchaseOnetimeProduct(productId) {
    return this.request(`/api/onetime-products/${productId}/purchase`, {
      method: 'POST'
    });
  }

  async updateOnetimeProduct(productId, data) {
    return this.request(`/api/admin/onetime-products/${productId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async getProductOrders(productId) {
    return this.request(`/api/admin/onetime-products/${productId}/orders`);
  }

  // PayPal Subscriptions
  async createPayPalPlans() {
    return this.request('/api/admin/paypal/create-plans', { method: 'POST' });
  }

  async getPayPalPlans() {
    return this.request('/api/admin/paypal/plans');
  }

  async createPayPalSubscription(tier, billingInterval) {
    return this.request('/api/paypal/create-subscription', {
      method: 'POST',
      body: JSON.stringify({ tier, billing_interval: billingInterval }),
    });
  }

  // Legacy Subscription (keep for backward compatibility)
  async createCheckout(plan, originUrl) {
    // Redirect to new order system
    console.warn('createCheckout is deprecated, use createOrder instead');
    return this.createOrder({
      order_type: 'subscription',
      items: [{
        product_type: 'subscription',
        product_name: plan,
        quantity: 1,
        unit_price: 20, // Will be determined by backend
        total_price: 20
      }],
      payment_method: 'paypal',
      discount: 0
    });
  }

  async getCheckoutStatus(sessionId) {
    return this.request(`/api/subscription/checkout-status/${sessionId}`);
  }

  async getSubscriptionStatus() {
    return this.request('/api/user/subscription/status');
  }

  async getSubscriptionSettings() {
    return this.request('/api/admin/subscription/settings');
  }

  async updateSubscriptionSettings(data) {
    return this.request('/api/admin/subscription/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getAllTransactions() {
    return this.request('/api/admin/subscription/transactions');
  }

  // Sub-Account Invitations
  async createInvitation(name) {
    return this.request('/api/invitations/create', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async getMyInvitations() {
    return this.request('/api/invitations/my');
  }

  async getSubAccounts() {
    return this.request('/api/invitations/sub-accounts');
  }

  async revokeInvitation(code) {
    return this.request(`/api/invitations/${code}`, {
      method: 'DELETE',
    });
  }

  async removeSubAccount(userId) {
    return this.request(`/api/sub-accounts/${userId}`, {
      method: 'DELETE',
    });
  }

  // Admin assign subscription
  async assignSubscription(userId, plan, durationDays = null) {
    return this.request('/api/admin/assign-subscription', {
      method: 'POST',
      body: JSON.stringify({ 
        user_id: userId, 
        plan, 
        duration_days: durationDays 
      }),
    });
  }

  // Proxy Cards
  async generateProxyCards(count) {
    return this.request('/api/admin/proxy-cards/generate', {
      method: 'POST',
      body: JSON.stringify({ count }),
    });
  }

  async getAllProxyCards() {
    return this.request('/api/admin/proxy-cards');
  }

  async adminReleaseCard(cardId) {
    return this.request(`/api/admin/proxy-cards/${cardId}`, {
      method: 'DELETE',
    });
  }

  async claimProxyCard(code) {
    return this.request('/api/proxy-cards/claim', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  async getMyProxyCards() {
    return this.request('/api/proxy-cards/my');
  }

  async assignCardFrequency(cardId, frequencyId) {
    return this.request('/api/proxy-cards/assign-frequency', {
      method: 'PUT',
      body: JSON.stringify({ card_id: cardId, frequency_id: frequencyId }),
    });
  }

  async releaseMyCard(cardId) {
    return this.request(`/api/proxy-cards/${cardId}/release`, {
      method: 'DELETE',
    });
  }

  // Recommendations
  async getCurrentRecommendation() {
    return this.request('/api/recommendations/current');
  }

  async dismissRecommendation(recommendationId) {
    return this.request(`/api/recommendations/${recommendationId}/dismiss`, {
      method: 'POST',
    });
  }

  async createRecommendation(data) {
    return this.request('/api/admin/recommendations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getAllRecommendations() {
    return this.request('/api/admin/recommendations');
  }

  async deleteRecommendation(recId) {
    return this.request(`/api/admin/recommendations/${recId}`, {
      method: 'DELETE',
    });
  }

  // ========================================
  // CHATBOT / ZENTA-BEGLEITER API METHODS
  // ========================================

  // Chat methods
  async sendChatMessage(message, language = 'de') {
    return this.request('/api/chat/send', {
      method: 'POST',
      body: JSON.stringify({ message, language }),
    });
  }

  async getChatHistory() {
    return this.request('/api/chat/history');
  }

  async getChatUsage() {
    return this.request('/api/chat/usage');
  }

  // Mood methods
  async logMood(frequencyId, moodAfter) {
    return this.request('/api/mood/log', {
      method: 'POST',
      body: JSON.stringify({ frequency_id: frequencyId, mood_after: moodAfter }),
    });
  }

  async getMoodHistory() {
    return this.request('/api/mood/history');
  }

  // Admin Chatbot methods
  async getChatbotSettings() {
    return this.request('/api/admin/chatbot/settings');
  }

  async updateChatbotSettings(settings) {
    return this.request('/api/admin/chatbot/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  async testChatbotConnection() {
    return this.request('/api/admin/chatbot/test', {
      method: 'POST',
    });
  }

  async getChatbotUsageStats() {
    return this.request('/api/admin/chatbot/usage');
  }

  async getChatbotUsageByUser() {
    return this.request('/api/admin/chatbot/usage/users');
  }

  // Proactive messages methods
  async getUnreadCount() {
    return this.request('/api/chat/unread-count');
  }

  async markMessagesRead() {
    return this.request('/api/chat/mark-read', {
      method: 'POST',
    });
  }

  async toggleProactiveMessages(enabled) {
    return this.request(`/api/user/preferences/proactive?enabled=${enabled}`, {
      method: 'POST',
    });
  }

  async getProactivePreference() {
    return this.request('/api/user/preferences/proactive');
  }

  // Daily Check-in API methods
  async getDailyCheckinData() {
    return this.request('/api/user/daily-checkin');
  }

  async logDailyCheckin(mood) {
    return this.request('/api/user/daily-checkin', {
      method: 'POST',
      body: JSON.stringify({ mood }),
    });
  }

  async skipDailyCheckin() {
    return this.request('/api/user/daily-checkin/skip', {
      method: 'POST',
    });
  }

  async shouldShowDailyCheckin() {
    return this.request('/api/user/daily-checkin/should-show');
  }

  // Practitioner API methods
  async becomePractitioner(practitionerName, specialization, companyName) {
    return this.request('/api/user/become-practitioner', {
      method: 'POST',
      body: JSON.stringify({ 
        practitioner_name: practitionerName,
        specialization: specialization || '',
        company_name: companyName || ''
      }),
    });
  }

  async requestAccess(zenId) {
    return this.request('/api/practitioner/request-access', {
      method: 'POST',
      body: JSON.stringify({ zen_id: zenId }),
    });
  }

  async getPendingAccessRequests() {
    return this.request('/api/user/pending-access-requests');
  }

  async respondToAccessRequest(requestId, approved, permissions) {
    return this.request(`/api/user/respond-to-access-request/${requestId}`, {
      method: 'POST',
      body: JSON.stringify({ approved, permissions }),
    });
  }

  async getMyClients() {
    return this.request('/api/practitioner/my-clients');
  }

  async getMyPractitioners() {
    return this.request('/api/user/my-practitioners');
  }

  async removeClient(connectionId) {
    return this.request(`/api/practitioner/remove-client/${connectionId}`, {
      method: 'DELETE',
    });
  }

  async revokePractitionerAccess(connectionId) {
    return this.request(`/api/user/revoke-practitioner-access/${connectionId}`, {
      method: 'DELETE',
    });
  }

  async getClientStats(clientId) {
    return this.request(`/api/practitioner/client/${clientId}/stats`);
  }

  async getClientJournal(clientId) {
    return this.request(`/api/practitioner/client/${clientId}/journal`);
  }

  async getClientMoodLogs(clientId) {
    return this.request(`/api/practitioner/client/${clientId}/mood-logs`);
  }

  // Practitioner Recommendations
  async createFrequencyRecommendation(clientUserId, frequencyId, title, description) {
    return this.request('/api/practitioner/recommendations', {
      method: 'POST',
      body: JSON.stringify({ client_user_id: clientUserId, frequency_id: frequencyId, title, description }),
    });
  }

  async getClientRecommendations(clientId) {
    return this.request(`/api/practitioner/client/${clientId}/recommendations`);
  }

  async getUserRecommendations() {
    return this.request('/api/user/my-recommendations');
  }

  async updateRecommendationStatus(recId, status) {
    return this.request(`/api/user/recommendations/${recId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  async deletePractitionerRecommendation(recId) {
    return this.request(`/api/practitioner/recommendations/${recId}`, {
      method: 'DELETE',
    });
  }

  // Session Notes
  async createSessionNote(clientUserId, noteContent, date) {
    return this.request('/api/practitioner/session-notes', {
      method: 'POST',
      body: JSON.stringify({ client_user_id: clientUserId, note_content: noteContent, date }),
    });
  }

  async getClientSessionNotes(clientId) {
    return this.request(`/api/practitioner/client/${clientId}/session-notes`);
  }

  async updateSessionNote(noteId, noteContent) {
    return this.request(`/api/practitioner/session-notes/${noteId}`, {
      method: 'PUT',
      body: JSON.stringify({ note_content: noteContent }),
    });
  }

  async deleteSessionNote(noteId) {
    return this.request(`/api/practitioner/session-notes/${noteId}`, {
      method: 'DELETE',
    });
  }

  // Admin Practitioner Management
  async assignPractitionerRole(userId) {
    return this.request(`/api/admin/users/${userId}/assign-practitioner-role`, {
      method: 'POST',
    });
  }

  async removePractitionerRole(userId) {
    return this.request(`/api/admin/users/${userId}/remove-practitioner-role`, {
      method: 'DELETE',
    });
  }

  // Admin Affiliate Management
  async getAffiliateCategories() {
    return this.request('/api/admin/affiliate/categories');
  }

  async createAffiliateCategory(data) {
    return this.request('/api/admin/affiliate/categories', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAffiliateCategory(categoryId, data) {
    return this.request(`/api/admin/affiliate/categories/${categoryId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteAffiliateCategory(categoryId) {
    return this.request(`/api/admin/affiliate/categories/${categoryId}`, {
      method: 'DELETE',
    });
  }

  async getAffiliateSettings() {
    return this.request('/api/admin/affiliate/settings');
  }

  async updateAffiliateSettings(data) {
    return this.request('/api/admin/affiliate/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getAllCouponCodes() {
    return this.request('/api/admin/affiliate/coupon-codes');
  }

  async createCouponCode(data) {
    return this.request('/api/admin/affiliate/coupon-codes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async toggleCouponCode(codeId) {
    return this.request(`/api/admin/affiliate/coupon-codes/${codeId}/toggle`, {
      method: 'PUT',
    });
  }

  async deleteCouponCode(codeId) {
    return this.request(`/api/admin/affiliate/coupon-codes/${codeId}`, {
      method: 'DELETE',
    });
  }

  async getAllAffiliates() {
    return this.request('/api/admin/affiliate/all-affiliates');
  }

  async assignAffiliateCategory(affiliateUserId, categoryId) {
    return this.request(`/api/admin/affiliate/${affiliateUserId}/assign-category`, {
      method: 'POST',
      body: JSON.stringify({ category_id: categoryId }),
    });
  }

  async getAllPayouts() {
    return this.request('/api/admin/affiliate/payouts');
  }

  async updatePayoutStatus(payoutId, status, adminNotes) {
    return this.request(`/api/admin/affiliate/payouts/${payoutId}`, {
      method: 'PUT',
      body: JSON.stringify({ status, admin_notes: adminNotes }),
    });
  }

  // User Affiliate
  async becomeAffiliate(paymentMethod, iban, paypalEmail, accountHolderName) {
    return this.request('/api/user/become-affiliate', {
      method: 'POST',
      body: JSON.stringify({
        payment_method: paymentMethod,
        iban: iban || null,
        paypal_email: paypalEmail || null,
        account_holder_name: accountHolderName
      }),
    });
  }

  async getMyAffiliateProfile() {
    return this.request('/api/user/my-affiliate-profile');
  }

  async getMyReferrals() {
    return this.request('/api/user/my-referrals');
  }

  async getMyEarnings() {
    return this.request('/api/user/my-earnings');
  }

  async requestPayout(amount) {
    return this.request('/api/user/request-payout', {
      method: 'POST',
      body: JSON.stringify({ amount }),
    });
  }

  async getMyPayouts() {
    return this.request('/api/user/my-payouts');
  }

  // New Affiliate API methods
  async updateAffiliatePaymentDetails(paymentMethod, iban, paypalEmail, accountHolderName) {
    return this.request('/api/user/affiliate-payment-details', {
      method: 'PUT',
      body: JSON.stringify({
        payment_method: paymentMethod,
        iban: iban,
        paypal_email: paypalEmail,
        account_holder_name: accountHolderName,
      }),
    });
  }

  async validateReferralCode(refCode) {
    return this.request(`/api/affiliate/validate-ref/${refCode}`);
  }

  async trackAffiliateClick(affiliateCode) {
    return this.request(`/api/affiliate/track-click/${affiliateCode}`, {
      method: 'POST',
    });
  }

  async getAffiliateStats() {
    return this.request('/api/admin/affiliate/stats');
  }

  async getAffiliateDetails(userId) {
    return this.request(`/api/admin/affiliate/${userId}/details`);
  }

  // Email System APIs
  async requestPasswordReset(email) {
    return this.request('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token, newPassword) {
    return this.request('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, new_password: newPassword }),
    });
  }

  async getEmailSettings() {
    return this.request('/api/admin/email-settings');
  }

  async updateEmailSettings(settings) {
    return this.request('/api/admin/email-settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  async sendTestEmail(toEmail) {
    return this.request('/api/admin/email-test', {
      method: 'POST',
      body: JSON.stringify({ to_email: toEmail }),
    });
  }

  async getEmailTemplates() {
    return this.request('/api/admin/email-templates');
  }

  async updateEmailTemplate(templateType, data) {
    return this.request(`/api/admin/email-templates/${templateType}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getEmailLog() {
    return this.request('/api/admin/email-log');
  }

  // Offline Audio APIs
  async getOfflineAudio() {
    return this.request('/api/admin/offline-audio');
  }

  async uploadOfflineAudio(audioFile) {
    const formData = new FormData();
    formData.append('audio', audioFile);
    return this.request('/api/admin/offline-audio/upload', {
      method: 'POST',
      body: formData,
      isFormData: true,
    });
  }

  async deleteOfflineAudio(filename) {
    return this.request(`/api/admin/offline-audio/${filename}`, {
      method: 'DELETE',
    });
  }

  async getOfflineAudioList() {
    return this.request('/api/offline-audio-list');
  }

  // Push Notification APIs
  async getVapidPublicKey() {
    return this.request('/api/vapid-public-key');
  }

  async subscribeToPush(subscription) {
    return this.request('/api/user/push-subscription', {
      method: 'POST',
      body: JSON.stringify(subscription),
    });
  }

  async unsubscribeFromPush() {
    return this.request('/api/user/push-subscription', {
      method: 'DELETE',
    });
  }

  async getNotificationSettings() {
    return this.request('/api/user/notification-settings');
  }

  async updateNotificationSettings(settings) {
    return this.request('/api/user/notification-settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }
}

const apiClient = new ApiClient();
export default apiClient;
